const MESSAGE_ALERT_SOUND_ENABLED_KEY = "messageAlertSoundEnabled";
const MIN_ALERT_GAP_MS = 1500;

type AudioWindow = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };

let audioContext: AudioContext | undefined;
let lastPlayedAt = 0;

const getAudioContext = (): AudioContext | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  if (audioContext) {
    return audioContext;
  }

  const AudioContextConstructor =
    window.AudioContext || (window as AudioWindow).webkitAudioContext;

  if (!AudioContextConstructor) {
    return undefined;
  }

  audioContext = new AudioContextConstructor();
  return audioContext;
};

export const getMessageAlertSoundEnabled = (): boolean =>
  localStorage.getItem(MESSAGE_ALERT_SOUND_ENABLED_KEY) !== "false";

export const setMessageAlertSoundEnabled = (enabled: boolean): void => {
  localStorage.setItem(MESSAGE_ALERT_SOUND_ENABLED_KEY, String(enabled));
};

export const unlockMessageAlertSound = async (): Promise<boolean> => {
  const context = getAudioContext();

  if (!context) {
    return false;
  }

  if (context.state === "suspended") {
    await context.resume();
  }

  return context.state === "running";
};

const playTone = (
  context: AudioContext,
  startTime: number,
  frequency: number,
  duration: number,
  volume: number
) => {
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(frequency, startTime);

  gain.gain.setValueAtTime(0.0001, startTime);
  gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(startTime);
  oscillator.stop(startTime + duration + 0.02);
};

export const playMessageAlertSound = async (
  force = false
): Promise<boolean> => {
  if (!force && !getMessageAlertSoundEnabled()) {
    return false;
  }

  const nowMs = Date.now();
  if (!force && nowMs - lastPlayedAt < MIN_ALERT_GAP_MS) {
    return false;
  }

  try {
    const unlocked = await unlockMessageAlertSound();
    const context = getAudioContext();

    if (!unlocked || !context) {
      return false;
    }

    const now = context.currentTime;
    playTone(context, now, 740, 0.11, 0.035);
    playTone(context, now + 0.12, 980, 0.13, 0.03);
    lastPlayedAt = nowMs;

    return true;
  } catch (error) {
    console.warn("Message alert sound could not play", error);
    return false;
  }
};

export const initMessageAlertSound = (): (() => void) | undefined => {
  if (typeof window === "undefined" || !getMessageAlertSoundEnabled()) {
    return undefined;
  }

  const unlock = () => {
    unlockMessageAlertSound().catch(() => undefined);
  };
  const options = { once: true, passive: true };

  window.addEventListener("pointerdown", unlock, options);
  window.addEventListener("keydown", unlock, options);
  window.addEventListener("touchstart", unlock, options);

  return () => {
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("touchstart", unlock);
  };
};
